import React, {useContext} from 'react';
import {Audit} from "@bdfx/audit-api/types"
import {
  Table,
  Td, Th, Tr,
  Div,
  css,
  Button,
  Icon,
  Paginator,
} from "@bdfx/fukit"
import { formatPrice, formatHeurekaDelivery, } from "../../common/format"

import {AuditContext} from "../providers/AuditProvider"
import {ProductsContext} from "../providers/ProductsProvider"
import {getFilters} from "../helpers"
import ctrans from "../../common/trans"

export const colorAvailability = (days) => days < 4 ? "green" : days < 31 ? "#333" : "#888"

export const fields = {
    "single_shop": [],
    "no_shipping_price": ["PRICE_POSITION","BIDDED_POSITION","SHOPS_COUNT"],
    "not_paired": [],
    "zero_price": [],
    "competitors_with_same_price_with_free_shipping": ["DELIVERY_PRICE","PRICE_POSITION","BIDDED_POSITION","SHOPS_COUNT"],
    "competitors_with_same_price_with_gift": ["PRICE_POSITION","BIDDED_POSITION","SHOPS_COUNT"],
    "more_than_10_position": ["PRICE_POSITION","BIDDED_POSITION","SHOPS_COUNT"],
    "is_most_expansive": ["PRICE_POSITION","BIDDED_POSITION","SHOPS_COUNT"],
    "similar_but_better_availability": ["PRICE_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "p130_of_cheapest": ["PRICE_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "competitors_below_purchase_price": ["PURCHASE_PRICE","PRICE_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "few_shops": ["PRICE_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "my_price_below_purchase_price": ["PURCHASE_PRICE","PRICE_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "not_in_bidding": ["PRICE_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "most_expensive_in_bidding": ["BIDDED_POSITION","PRICE_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "cheapest_in_bidding": ["BIDDED_POSITION","PRICE_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "first_and_too_cheap": ["PRICE_POSITION","BIDDED_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "cheapest": ["PRICE_POSITION","BIDDED_POSITION","SHOPS_COUNT","DELIVERY_PRICE",],
    "group_ok": [],
    "group_problems": [],
    "group_warnings": [],
    "group_recommendations": [],
  }
  
export const commonFields = ["IMAGE","PRODUCT_NAME","ITEM_ID","AVAILABILITY_DAYS","PRICE_VAT"]
export const exportFields = ["HEUREKA_CATEGORY","HEUREKA_URL","URL",]
  

const formatCell = (field, product, locale) => {
    switch(field) {
        case "image": 
            return (product.image ? <Td width={"80px"}><img alt={`${ctrans("product")}: ${product.product_name}`} src={product.image} key={product.product_name+"_image"} /></Td> : <Td></Td>)
        case "product_name":
            return <Td aria-label={ctrans("name")} width={"50%"} align="left" key={product.product_name}>
                        <a href={product.url} target="_blank" rel="nofollow, noreferrer">{product.product_name}</a><br/>
                        <Icon type="tag"><Div as="span" color="#888" title="ITEM_ID">{product.item_id}</Div></Icon>
                    </Td>
        case "item_id":
            return null
        case "price_vat":
        case "purchase_price":
        case "delivery_price":
            return <Td aria-label={ctrans(field.toUpperCase())} key={product.product_name+"_price"}>{formatPrice(product[field], locale)}</Td>
        case "availability_days": 
            return <Td aria-label={ctrans(field.toUpperCase())} key={product.product_name+"_availability"}><Div as="span" color={colorAvailability(product.availability_days)}>{formatHeurekaDelivery(product.availability_days)}</Div></Td>
        default:
            return <Td aria-label={ctrans(field.toUpperCase())} key={product.product_name+"_"+field}>{product[field]}</Td>
    }
}

function Products() {

    const [audit]: [Audit] = useContext(AuditContext)
    const [products,getProducts, loading]: any = useContext(ProductsContext)
    const index: string = getFilters()
    
    const {data } = products
    const { page, per_page, total, } = data
    const locale = audit.shop?.heureka_locale
    const columns = [...commonFields,...fields[index]]
    if(data.products.length === 0) {
        return <Div minHeight={"300px"} padding={"50px"} fontStyle={"italic"} fontSize={"1.3rem"} textAlign="center">{ctrans("no_products_found")}</Div>
    }
    return (
        <Style>
            <Div textAlign="center" padding="5px" className={loading ? "fuk-helper-loading" : ""}>
                <Paginator pages={Math.round(total/per_page)} current={page} show={5} onChange={getProducts} />
            </Div>
            <Table className="products" header={[...columns.filter(f => f !== "ITEM_ID").map(field => {
                    if(field === "PRODUCT_NAME") { 
                        return <Th align="left">{ctrans("name")}</Th>
                    }               
                    return ctrans(field) 
                }),
                ctrans("actions"),
            ]}>
                {data.products.map(p => {
                    return (
                        <Tr key={p.item_id}>
                            {columns.map(field => formatCell(field.toLowerCase(), p, locale))}
                            <Td className="actions" width={"1%"} key={p.item_id+"_actions"}>
                                <Button size="small" as={"a"} color="#3c3c3c" href={p.heureka_url} target="_blank" rel={"nofollow"} secondary={true} icon="heureka-small">Heureka</Button>
                                &nbsp;
                                <Button size="small" as={"a"} color="#3c3c3c" href={p.url} target="_blank" rel={"nofollow"} secondary={true} icon="external-link">E-shop</Button>
                            </Td>
                        </Tr> 
                    )
                })}                                                     
            </Table>
            <Div textAlign="center" padding="5px" className={loading ? "fuk-helper-loading" : ""}>
                <Paginator pages={Math.round(total/per_page)} current={page} show={5} onChange={getProducts} />
            </Div>
        </Style>
  );
}

const Style = css("div")({

    "@media only screen and (max-width: 1000px)": {
        "thead": {
            display: "none",
        },
        "table, tbody, tr, td": {
            width: "100%",
            display: "block",
            textAlign: "right!important",
        },
        "img": {
            margin: "10px !important",
        },
        "td:before": {
            content: 'attr(aria-label)',
            float: "left",
            color: "#888",
        },
        ".actions": {
            width: "100%",
            texAlign: "right",
            padding: "20px",
            paddingRight: "5px !important",
        }
    },

    ".actions": {
        whiteSpace: "nowrap", 
        " a": {
            textDecoration: "none"
        },
    },

    "th": {
        whiteSpace: "nowrap",
    },
})

export default Products;